import { useMemo } from 'react';

import { useOrderTrackerStatus } from '#client/hooks/useOrderTrackerStatus';

const ORDER_TASK_TYPES = {
  LLC_FORMATION: 'llc_formation',
  CORP_FORMATION: 'corp_formation',
  CHANGE_OF_REGISTERED_AGENT: 'change_of_registered_agent',
};

const TASK_STATUS = {
  COMPLETED: 'completed',
};

const DEFAULT_FORMATION_FLAGS = {
  isFormedByZB: false,
  isFormationComplete: false,
  isCora: false,
  isCoraComplete: false,
};

/**
 * Helper function to safely find a task by type
 * @param {Array} tasks - Array of order tasks
 * @param {string} taskType - Type of task to find
 * @returns {object | undefined} Found task or undefined
 */
const findTaskByType = (tasks, taskType) => {
  if (!Array.isArray(tasks)) {
    return undefined;
  }
  return tasks.find((task) => task?.type === taskType);
};

/**
 * Helper function to check if a task has completed status
 * @param {object} task - Task object to check
 * @returns {boolean} True if task exists and is completed
 */
const isTaskCompleted = (task) => !!(task && task.status === TASK_STATUS.COMPLETED);

/**
 * Hook to determine formation status based on order tasks
 * Returns boolean flags for different formation states
 *
 * @returns {object} Formation status object with boolean flags:
 *   - loading: boolean - Whether data is still loading
 *   - isFormedByZB: boolean - Whether LLC or Corp formation tasks exist
 *   - isFormationComplete: boolean - Whether formation tasks are completed
 *   - isCora: boolean - Whether change_of_registered_agent task exists
 *   - isCoraComplete: boolean - Whether CORA task is completed
 */
export const useFormationStatus = () => {
  const { loading, orderTasksWithStatus } = useOrderTrackerStatus();

  const formationFlags = useMemo(() => {
    // Early return for loading or invalid data
    if (loading || !orderTasksWithStatus) {
      return DEFAULT_FORMATION_FLAGS;
    }

    // Find relevant tasks
    const llcFormationTask = findTaskByType(orderTasksWithStatus, ORDER_TASK_TYPES.LLC_FORMATION);
    const corpFormationTask = findTaskByType(orderTasksWithStatus, ORDER_TASK_TYPES.CORP_FORMATION);
    const coraTask = findTaskByType(orderTasksWithStatus, ORDER_TASK_TYPES.CHANGE_OF_REGISTERED_AGENT);

    // Calculate boolean flags
    const isFormedByZB = !!(llcFormationTask || corpFormationTask);
    const isFormationComplete = isTaskCompleted(llcFormationTask) || isTaskCompleted(corpFormationTask);
    const isCora = !!coraTask;
    const isCoraComplete = isTaskCompleted(coraTask);

    return {
      isFormedByZB,
      isFormationComplete,
      isCora,
      isCoraComplete,
    };
  }, [loading, orderTasksWithStatus]);

  return {
    loading,
    ...formationFlags,
  };
};
